import React from 'react';
import { Theme } from '@material-ui/core';
import { NOTIFICATION_SEMIBOLD_CLASS } from '../registerNotificationFonts';
import { formatNotificationRelativeTime } from './formatNotificationRelativeTime';

export type RouteMaxTheme = Theme & {
  textFontSize?: number;
  TextColor?: string;
  gridTextColor?: string;
  scrollThumbColor?: string;
  tooltipBGColor?: string;
};

export type CardLayoutClasses = Record<string, string>;

export type CardLayoutProps = {
  classes: CardLayoutClasses;
  icon: React.ReactNode;
  title: React.ReactNode;
  body: React.ReactNode;
  timestamp: string | Date;
  footer?: React.ReactNode;
  onClick?: () => void;
};

const CardLayout = ({
  classes,
  icon,
  title,
  body,
  timestamp,
  footer,
  onClick,
}: CardLayoutProps) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  const relativeTime = formatNotificationRelativeTime(timestamp);

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={classes.outerContainer}
    >
      <div className={classes.cardContainer}>
        <div className={classes.iconContainer}>{icon}</div>
        <div className={classes.info}>
          <div className={classes.titleRow}>
            <div className={`${classes.title} ${NOTIFICATION_SEMIBOLD_CLASS}`}>{title}</div>
            {relativeTime ? <div className={classes.time}>{relativeTime}</div> : null}
          </div>
          <div className={classes.body}>{body}</div>
          {footer ? <div className={classes.footer}>{footer}</div> : null}
        </div>
      </div>
    </div>
  );
};

export const getCardLayoutStyles = (theme: Theme) => {
  const routeMaxTheme = theme as RouteMaxTheme;

  return {
    cardContainer: {
      minHeight: 'auto',
      display: 'flex',
      alignItems: 'flex-start',
      padding: '10px 10px 10px 12px',
      width: '100%',
      boxSizing: 'border-box',
    },
    iconContainer: {
      flexShrink: 0,
      width: 34,
      marginRight: 12,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
    },
    info: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minWidth: 0,
      width: '100%',
    },
    outerContainer: {
      cursor: 'pointer',
      width: '100%',
      boxSizing: 'border-box',
      '&:not(:last-child)': {
        borderBottom: `1px solid ${routeMaxTheme.scrollThumbColor}`,
      },
      '&:only-child': {
        borderBottom: `1px solid ${routeMaxTheme.scrollThumbColor}`,
      },
      '&:hover': {
        backgroundColor: routeMaxTheme.tooltipBGColor,
      },
    },
    titleRow: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      columnGap: 12,
      alignItems: 'start',
      marginBottom: 8,
      width: '100%',
      boxSizing: 'border-box',
    },
    title: {
      fontSize: routeMaxTheme.textFontSize,
      color: routeMaxTheme.gridTextColor || routeMaxTheme.TextColor,
      fontWeight: 600,
      lineHeight: '18px',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    body: {
      fontSize: routeMaxTheme.textFontSize,
      color: routeMaxTheme.TextColor,
      lineHeight: '16px',
      marginBottom: 8,
      '&:last-child': {
        marginBottom: 0,
      },
    },
    footer: {
      fontSize: routeMaxTheme.textFontSize,
      lineHeight: '16px',
    },
    time: {
      fontSize: routeMaxTheme.textFontSize,
      color: routeMaxTheme.TextColor,
      lineHeight: '18px',
      opacity: '0.7',
      whiteSpace: 'nowrap',
      textAlign: 'right',
      justifySelf: 'end',
    },
  };
};

export default CardLayout;
